import { useCallback, useRef } from 'react';

import { Button } from '#/components/Button/Button';
import { closeDropdownFromElementWithin } from '#/components/Dropdown/closeDropdownFromElementWithin';
import { LocationInput } from '#/components/LocationInput/LocationInput';
import { H4 } from '#/components/Text/H4';
import { UncontrolledSearchQueryInputContainer } from '#/containers/UncontrolledSearchQueryInputContainer';
import { useKeyedState } from '#/hooks/useKeyedState';
import { useI18n } from '#/i18n/hooks/useI18n';
import { UncontrolledListingTypeSelect } from '#/modules/listing/components/ListingTypeSelect/UncontrolledListingTypeSelect';
import { SearchButton } from '#/modules/search/components/SearchButton';
import { searchTypes } from '#/modules/search/constants/searchTypes';
import { getPopularSearchesBySearchType } from '#/modules/search/mappings/getPopularSearchesBySearchType';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import type { SearchType } from '#/modules/search/types/SearchType';
import { colors } from '#/theme/theme';
import { trackEvent } from '#/utils/analytics/track/trackEvent';

import {
  MobileSearchMenuBody,
  MobileSearchMenuButtonWrapper,
  MobileSearchMenuForm,
  MobileSearchMenuHeader,
  MobileSearchMenuHolder,
} from './MobileSearchMenu.styled';
import { MobileSearchMenuPopularSearches } from './PopularSearches/MobileSearchMenuPopularSearches';

type Props = {
  defaultSearchType: SearchType;
  initialQuery: string;
  searchLocation: SearchLocation | undefined;
  onChangeSearchLocation: (
    newSearchLocation: SearchLocation | undefined,
  ) => boolean;
  onSearch: (data: {
    listingType: SearchType;
    query: string;
    triggeredByRemoteLocationChange: boolean;
  }) => void;
  searchOnClear: boolean;
};

export function MobileSearchMenu({
  defaultSearchType,
  initialQuery,
  searchLocation,
  onChangeSearchLocation,
  onSearch,
  searchOnClear,
}: Props) {
  const { i18n } = useI18n();

  const [searchTypeForPopularSearches, setSearchTypeForPopularSearches] =
    useKeyedState(defaultSearchType, {
      key: defaultSearchType,
    });

  const popularSearches = getPopularSearchesBySearchType(
    i18n,
    searchTypeForPopularSearches,
  );

  const formRef = useRef<HTMLFormElement>(null);
  const submit = useCallback(
    (options: { triggeredByRemoteLocationChange: boolean }) => {
      const form = formRef.current;
      if (!form) return;

      const formData = new FormData(form);
      onSearch({
        query: formData.get('q') as string,
        listingType: formData.get('listing-type') as SearchType,
        triggeredByRemoteLocationChange:
          options.triggeredByRemoteLocationChange,
      });

      closeDropdownFromElementWithin(form);
    },
    [onSearch],
  );

  return (
    <MobileSearchMenuHolder data-qa-id="mobile-search-menu">
      <MobileSearchMenuHeader>
        <H4 mb={0} color={colors.brandBlue}>
          {i18n('Search')}
        </H4>

        <Button
          type="button"
          qaId="close-mobile-search"
          onClick={(event) => {
            closeDropdownFromElementWithin(event.target as HTMLElement);
          }}
          aria-label={i18n('Close search')}
          variant="tertiary"
          noPadding
          icon={{ type: 'normalized', name: 'close' }}
        />
      </MobileSearchMenuHeader>

      <MobileSearchMenuBody>
        <MobileSearchMenuForm
          ref={formRef}
          onSubmit={(event) => {
            event.preventDefault();
            submit({ triggeredByRemoteLocationChange: false });
          }}
          data-qa-id="search-inputs"
        >
          <UncontrolledListingTypeSelect
            name="listing-type"
            defaultValue={defaultSearchType}
            searchTypes={searchTypes}
            variant="grey"
            onChange={(listingType) => {
              trackEvent('Changed Search Type', {
                filter_type: listingType,
                context: 'Mobile Search Menu',
              });
              setSearchTypeForPopularSearches(listingType);
            }}
          />

          <UncontrolledSearchQueryInputContainer
            initialQuery={initialQuery}
            placeholder={i18n('Search by keyword, skill, or interest')}
            onClearQuery={
              searchOnClear
                ? () => submit({ triggeredByRemoteLocationChange: false })
                : undefined
            }
            presentation="contrast"
            id="mobile-search-query"
            qaId="mobile-search-input"
            searchType={defaultSearchType}
          />

          <LocationInput
            affixVariant="transparent"
            styleVariant="contrast"
            hasRemoteOptions
            showLocationIcon
            showClearButton
            placeholder={i18n('Everywhere')}
            locationText={searchLocation?.text}
            onChange={(newLocation) => {
              // Avoid submitting twice - the LocationInput has a bug that triggers the onChange twice
              if (!onChangeSearchLocation(newLocation)) return;

              // Trigger search when search location changes
              submit({
                triggeredByRemoteLocationChange: Boolean(newLocation?.isRemote),
              });
            }}
            id="page-header-mobile-search-location"
          />
          <MobileSearchMenuButtonWrapper>
            <SearchButton />
          </MobileSearchMenuButtonWrapper>
        </MobileSearchMenuForm>
      </MobileSearchMenuBody>

      <MobileSearchMenuPopularSearches
        searchType={searchTypeForPopularSearches}
        popularSearches={popularSearches}
        onSearch={(event, query) => {
          closeDropdownFromElementWithin(event.target as HTMLElement);
          onSearch({
            listingType: searchTypeForPopularSearches,
            query,
            triggeredByRemoteLocationChange: false,
          });
        }}
      />
    </MobileSearchMenuHolder>
  );
}
